import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CtInterviewAnswerView, CtInterviewSubmissionDetail } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { exportSubmissionPdf } from '../../ct-interview/submissionPdf';

/**
 * One candidate's assessment, on its own page and its own URL.
 *
 * Laid out as the paper packet reads — sections in order, questions in order, the question above
 * the answer — because the interviewer is comparing what they have on screen with a conversation
 * they are about to have, not scanning a data structure. The questions shown are the ones this
 * candidate was actually given, snapshotted at submission time, so an assessment reworded since
 * still reads correctly.
 *
 * This page is also where the packet's fourth sheet lives — the one headed "INTERNAL USE ONLY — DO
 * NOT SHARE WITH CANDIDATE". The marking is done on the way in and read back off the answer rows,
 * so what is shown beside a wrong answer is the key as it read when that candidate sat it; the
 * candidate's own half of the section has no route that could ever serve any of it.
 *
 * What is shown here is the correct option and nothing more. The key's rationale — the packet's
 * "Why" column — is in the data and in the exported key PDF, but not on this page: it is guidance
 * for whoever is about to interview, read once, and repeating it under every missed question buried
 * the thing the page is actually for, which is what the candidate picked.
 */
export function SubmissionDetailPage() {
  const { api } = useHubContext();
  const { submissionId } = useParams();

  const [submission, setSubmission] = useState<CtInterviewSubmissionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /** Which of the two exports is running, so only the button that was pressed says so. */
  const [exporting, setExporting] = useState<'plain' | 'key' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!submissionId) return;

    const controller = new AbortController();

    (async () => {
      try {
        setSubmission(await api.getCtInterviewSubmission(submissionId, controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load the assessment.');
        }
      }
    })();

    return () => controller.abort();
  }, [api, submissionId]);

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load the assessment</h2>
        <p>{loadError}</p>
        <p>
          <Link to="/ct-interview-review">Back to all assessments</Link>
        </p>
      </div>
    );
  }

  if (!submission) {
    return <div className="page-message">Loading…</div>;
  }

  const loaded = submission;
  const submitted = new Date(loaded.submittedAtUtc);
  const unanswered = loaded.questionCount - loaded.answeredCount;

  /**
   * Two exports of the same assessment, differing in one thing: whether the answer key goes with it.
   *
   * The marking is in both — a candidate's score is about that candidate and is already the first
   * line of either document, so a copy that says 7 of 9 while refusing to say which two would be
   * less useful without being more discreet. What the key version adds is the sheet that is reusable
   * against every future candidate, and that is the one worth having to ask for.
   */
  async function exportPdf(includeKey: boolean) {
    setExporting(includeKey ? 'key' : 'plain');
    setExportError(null);

    try {
      // jsPDF arrives here as its own chunk, fetched on this click — see submissionPdf.ts.
      await exportSubmissionPdf(loaded, { includeKey });
    } catch (err) {
      console.error('Could not export the assessment as a PDF', err);
      setExportError('The PDF could not be generated. Try again, or reload the page.');
    } finally {
      setExporting(null);
    }
  }

  return (
    <div className="care-page care-page--wide">
      <Link className="care-back" to="/ct-interview-review">
        ← All assessments
      </Link>

      <header className="care-detail__header">
        <div>
          <h1 className="care-page__title">{loaded.candidateName}</h1>
          <p className="care-page__subtitle">
            {loaded.location} · {submitted.toLocaleDateString()}{' '}
            {submitted.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>

        {/* Two rows beside the two on the left, rather than one column of four stacked items: the
            counts read as a line of figures, which is how they are scanned, and the header stops
            being twice as tall on the right as on the left. */}
        <div className="interview-detail__meta">
          <div className="interview-detail__stats">
            <span className="interview-stat">
              <strong>
                {loaded.answeredCount} / {loaded.questionCount}
              </strong>{' '}
              answered
            </span>

            {/* The marked sections only — see the dashboard's Score column for why that is a
                separate number from the one beside it. */}
            {loaded.scoredCount > 0 && (
              <span className="interview-stat">
                <strong>
                  {loaded.correctCount} / {loaded.scoredCount}
                </strong>{' '}
                correct
              </span>
            )}

            {/* Amber, matching the dashboard's Incomplete tag and the per-question flag below — the
                same fact should not change colour depending on where you read it. */}
            {unanswered > 0 && <span className="care-tag care-tag--flush">{unanswered} left blank</span>}
          </div>

          <div className="interview-detail__actions">
            {/* The plain export first and the key one second, so the copy that is safe to pass
                around is the one reached without thinking about it. */}
            <button
              type="button"
              className="button button--quiet"
              disabled={exporting !== null}
              onClick={() => void exportPdf(false)}
            >
              {exporting === 'plain' ? 'Preparing…' : 'Export PDF'}
            </button>
            <button
              type="button"
              className="button button--quiet"
              disabled={exporting !== null}
              onClick={() => void exportPdf(true)}
            >
              {exporting === 'key' ? 'Preparing…' : 'Export with answer key'}
            </button>
          </div>
        </div>
      </header>

      {exportError && (
        <div className="issues__block issues__block--error">
          <p>{exportError}</p>
        </div>
      )}

      {loaded.sections.map((section) => (
        <section className="care-review__group" key={section.title}>
          <h2 className="interview__section-title">{section.title}</h2>

          {section.answers.map((answer, index) => (
            <Answer key={answer.questionId} answer={answer} number={index + 1} />
          ))}
        </section>
      ))}

      {loaded.retiredAnswers.length > 0 && (
        <section className="care-review__group">
          <h2 className="interview__section-title">No longer asked</h2>
          <p className="care-page__subtitle">
            The questionnaire changed while this candidate was answering. Their answers are kept, but
            the questions that prompted them are not on the current form.
          </p>

          {loaded.retiredAnswers.map((answer) => (
            <Answer key={answer.questionId} answer={answer} questionLabel={answer.questionId} />
          ))}
        </section>
      )}
    </div>
  );
}

function Answer({
  answer,
  number,
  questionLabel,
}: {
  answer: CtInterviewAnswerView;

  /** Position within its section, or null for a retired answer, which has no place in one. */
  number?: number | null;

  /** Shown instead of the stored prompt, for a retired answer whose prompt is a placeholder. */
  questionLabel?: string;
}) {
  const blank = answer.answerText === null;

  // Shown for anything that did not score, which is the wrong answers and the skipped ones. A
  // correct answer needs no key printed under it — it is already on the line above.
  const key = answer.isCorrect === true ? null : answer.key;

  /*
   * The panel is coloured by how the answer did, not by the fact that it is an answer.
   *
   * It used to be mint-and-green whatever was in it, which read as approval: a candidate who picked
   * "turn the power off at the breaker" got their wrong answer presented in the same green as a
   * right one, with a small "Incorrect" pill somewhere above it losing the argument. Green now means
   * correct and nothing else — which is also why a written answer, where there is nothing to be
   * right about, drops to neutral rather than keeping a colour it cannot earn.
   */
  const tone =
    answer.isCorrect === null ? '' : answer.isCorrect ? ' interview-answer__text--right' : ' interview-answer__text--wrong';

  return (
    // Question and answer both carry weight, separated by treatment rather than by size: a dark
    // heading above a tinted panel with the green accent. An answer read without its question in
    // front of you is not assessable, so neither half is allowed to recede.
    <div className={blank ? 'interview-answer interview-answer--blank' : 'interview-answer'}>
      <p className="interview-answer__question">
        {number != null && <span className="interview-answer__number">Q{number}</span>}
        <span className="interview-answer__prompt">{questionLabel ?? answer.questionPrompt}</span>
        {answer.isCorrect !== null && (
          <span className={answer.isCorrect ? 'interview-mark interview-mark--right' : 'interview-mark interview-mark--wrong'}>
            {answer.isCorrect ? 'Correct' : 'Incorrect'}
          </span>
        )}
      </p>

      {/* The flag sits where the answer would have been — its own line under the question, never
          trailing it — so the eye finds it in the same place on every card, answered or not.
          Pre-wrap on the answer, not a paragraph: candidates write in lines and lists, and
          collapsing their line breaks turns a structured answer into a wall. */}
      {blank ? (
        <p className="interview-answer__flag">Not answered</p>
      ) : (
        <p className={`interview-answer__text${tone}`}>
          {/* The letter in front of the option, because that is how a multiple-choice answer gets
              talked about afterwards — and the text after it, because nobody reading this six weeks
              later remembers what C was. */}
          {answer.selectedOptionId !== null && (
            <span className="interview-answer__letter">{answer.selectedOptionId}</span>
          )}
          {answer.answerText}
        </p>
      )}

      {/* The correct option, and only that. The key's "Why" is in the exported key PDF instead —
          see the note at the top of this file. */}
      {key && (
        <div className="interview-key">
          <p className="interview-key__label">Correct answer</p>
          <p className="interview-key__answer">
            <span className="interview-answer__letter interview-answer__letter--right">
              {key.correctOptionId}
            </span>
            {key.correctOptionText}
          </p>
        </div>
      )}
    </div>
  );
}
